import type { LocalPrintDeviceRequest, LocalPrintPriorityResponse } from "@/api/business/types/printing"
import { reactive, readonly, watch } from "vue"
import { getLocalPrintPriority, heartbeatLocalPrintDevice, reportLocalPrintPresence, setLocalPrintPriority } from "@/api/business/printing"
import { isHqScope } from "@/router/hqAccess"
import { useUserStore } from "@/store/modules/user"
import { useViewScopeStoreHook } from "@/store/modules/viewScope"
import { hardwareBridge } from "@/utils/hardwareBridge"

const state = reactive({
  config: null as LocalPrintPriorityResponse | null,
  device: null as LocalPrintDeviceRequest | null,
  error: "",
  supported: true,
})
export const localPrintPriorityState = readonly(state)

export class LocalPrintScopeChangedError extends Error {
  constructor() { super("打印期间登录店铺已变更") }
}

function scopeKey() {
  const user = useUserStore()
  const view = useViewScopeStoreHook()
  return user.token && user.shopId && !isHqScope(view.activeScope, view.activeScopeId)
    ? `${user.token}:${user.shopId}:${view.activeScopeId ?? ""}`
    : ""
}

let activeScope = ""
let generation = 0
let pending: { scope: string, promise: Promise<PrioritySnapshot> } | null = null
const DEVICE_SYNC_INTERVAL_MS = 10_000
const CONFIG_SYNC_INTERVAL_MS = 60_000
const IDLE_PROBE_INTERVAL_MS = 30_000
let lastSyncAt = Number.NEGATIVE_INFINITY
let lastConfigAt = Number.NEGATIVE_INFINITY
let cachedDevice: LocalPrintDeviceRequest | null = null
let lastLocalReadAt = Number.NEGATIVE_INFINITY
let helperAvailable: boolean | undefined
let noLocalPrinters = false
let invalidationVersion = 0
let retryAfter = 0
let refreshError: unknown

export interface PrioritySnapshot {
  configured: boolean
  batchClaimSupported?: boolean
  device: LocalPrintDeviceRequest | null
  assertCurrentScope: () => void
  helperAvailable?: boolean
  noLocalPrinters?: boolean
}

export function invalidateLocalPrintPriority() {
  invalidationVersion++
  lastSyncAt = Number.NEGATIVE_INFINITY
  lastConfigAt = Number.NEGATIVE_INFINITY
  retryAfter = 0
}

function validateConfig(value: LocalPrintPriorityResponse | undefined): LocalPrintPriorityResponse {
  if (!value || typeof value.configured !== "boolean" || typeof value.preferredOnline !== "boolean"
    || !Number.isSafeInteger(value.revision) || Number(value.revision) < 0
    || (value.preferredDeviceId != null && typeof value.preferredDeviceId !== "string")) {
    throw new Error("优先打印配置读取异常，请稍后重试")
  }
  return value
}

function checkResponse(response: IApiResponseData<LocalPrintPriorityResponse>) {
  if (response.code !== 0) throw new Error(response.msg || "优先打印配置请求失败")
  return validateConfig(response.data)
}

function publish(config: LocalPrintPriorityResponse) {
  if (Number(config.revision) >= Number(state.config?.revision ?? 0)) state.config = config
}

export function publishLocalPrintBatchPriority(config: LocalPrintPriorityResponse | undefined, assertScope: () => void) {
  assertScope()
  publish(validateConfig(config))
  lastConfigAt = Date.now()
}

export function refreshLocalPrintPriority(force = true): Promise<PrioritySnapshot> {
  const scope = scopeKey()
  if (scope !== activeScope) {
    activeScope = scope
    generation++
    state.config = null
    state.device = null
    state.error = ""
    state.supported = true
    pending = null
    cachedDevice = null
    lastLocalReadAt = Number.NEGATIVE_INFINITY
    helperAvailable = undefined
    noLocalPrinters = false
    invalidateLocalPrintPriority()
  }
  const version = generation
  const assertCurrentScope = () => {
    if (!scope || scope !== scopeKey() || version !== generation) throw new LocalPrintScopeChangedError()
  }
  if (!scope) return Promise.resolve({ configured: false, device: null, assertCurrentScope })
  if (pending?.scope === scope) return pending.promise
  const snapshot = (): PrioritySnapshot => ({
    configured: state.config?.configured === true,
    batchClaimSupported: state.config?.batchClaimSupported === true,
    device: state.device,
    helperAvailable,
    noLocalPrinters,
    assertCurrentScope,
  })
  if (!force && Date.now() < retryAfter) return Promise.reject(refreshError)
  const interval = helperAvailable === false || noLocalPrinters ? IDLE_PROBE_INTERVAL_MS : DEVICE_SYNC_INTERVAL_MS
  if (!force && Date.now() - lastSyncAt < interval - 250) return Promise.resolve(snapshot())
  const syncStartedAt = Date.now()
  const requestInvalidation = invalidationVersion
  const markSynced = () => {
    if (requestInvalidation === invalidationVersion) lastSyncAt = syncStartedAt
  }
  const shopId = String(useUserStore().shopId)
  const promise = (async (): Promise<PrioritySnapshot> => {
    try {
      const connected = await hardwareBridge.canPrint()
      assertCurrentScope()
      helperAvailable = connected
      if (!connected) {
        state.device = null
        noLocalPrinters = false
        cachedDevice = null
        lastLocalReadAt = Number.NEGATIVE_INFINITY
        state.error = "请打开本机打印助手"
        markSynced()
        return snapshot()
      }
      let localError: unknown
      try {
        if (force || !cachedDevice || Date.now() - lastLocalReadAt >= IDLE_PROBE_INTERVAL_MS) {
          const deviceId = (!force && cachedDevice?.deviceId) || (await hardwareBridge.getLocalPrintDeviceContext(shopId)).deviceId
          assertCurrentScope()
          const mappings = await hardwareBridge.getPrinterMappings()
          assertCurrentScope()
          cachedDevice = { deviceId, localPrinterKeys: [...new Set(mappings.filter(item => item.available).map(item => item.type))] }
          lastLocalReadAt = Date.now()
        }
        noLocalPrinters = cachedDevice.localPrinterKeys.length === 0
        if (!force && noLocalPrinters && !state.device?.localPrinterKeys.length) {
          state.device = cachedDevice
          state.error = "请先在打印助手中配置可用打印机"
          markSynced()
          return snapshot()
        }
      } catch (error) {
        assertCurrentScope()
        localError = error
        cachedDevice = null
        noLocalPrinters = false
      }
      const modernConfigured = state.config?.configured && state.config.batchClaimSupported
      if (force || !Number.isFinite(lastConfigAt) || (!modernConfigured && Date.now() - lastConfigAt >= CONFIG_SYNC_INTERVAL_MS)) {
        let response
        try {
          response = await getLocalPrintPriority()
        } catch (error) {
          assertCurrentScope()
          const status = (error as { response?: { status?: number } })?.response?.status
          // 明确不支持时保留旧协议；不能把临时失败降级成未配置。
          if ((status === 404 || status === 405) && !state.config?.configured) {
            state.supported = false
            state.device = null
            state.error = "当前服务暂不支持优先打印设置"
            if (requestInvalidation === invalidationVersion) lastConfigAt = Date.now()
            markSynced()
            return snapshot()
          }
          throw error
        }
        assertCurrentScope()
        publish(checkResponse(response))
        state.supported = true
        if (requestInvalidation === invalidationVersion) lastConfigAt = Date.now()
      }
      if (!state.supported) {
        markSynced()
        return snapshot()
      }
      state.error = ""
      try {
        if (localError) throw localError
        if (!cachedDevice) throw new Error("无法读取本机打印身份")
        const device = cachedDevice
        noLocalPrinters = device.localPrinterKeys.length === 0
        // 只有有效现代映射才能证明无设备；首次无设备不持续写心跳。
        // 已在线的设备撤销映射时上报一次，尽快释放其优先级占位。
        if (!noLocalPrinters || state.device?.localPrinterKeys.length) {
          if (state.config?.configured && state.config.batchClaimSupported) {
            const response = await reportLocalPrintPresence(device)
            assertCurrentScope()
            if (response.code !== 0 || response.data !== true) throw new Error(response.msg || "上报本机打印状态失败")
          } else {
            const heartbeat = await heartbeatLocalPrintDevice(device)
            assertCurrentScope()
            publish(checkResponse(heartbeat))
            if (requestInvalidation === invalidationVersion) lastConfigAt = Date.now()
          }
        }
        state.device = device
        if (!device.localPrinterKeys.length) state.error = "请先在打印助手中配置可用打印机"
      } catch (error) {
        assertCurrentScope()
        state.device = null
        noLocalPrinters = false
        state.error = error instanceof Error ? error.message : "请检查打印助手是否支持固定设备身份"
      }
      assertCurrentScope()
      markSynced()
      retryAfter = 0
      return snapshot()
    } catch (error) {
      if (scope === scopeKey() && version === generation) {
        state.device = null
        state.error = error instanceof Error ? error.message : "无法同步优先打印配置"
        refreshError = error
        retryAfter = Date.now() + DEVICE_SYNC_INTERVAL_MS
      }
      throw error
    }
  })().finally(() => {
    if (pending?.promise === promise) pending = null
  }).then((value) => {
    assertCurrentScope()
    return requestInvalidation === invalidationVersion ? value : refreshLocalPrintPriority(false)
  })
  pending = { scope, promise }
  return promise
}

export async function chooseLocalPrintPriority(preferred: boolean, deviceName: string) {
  // 使用用户看到的版本，禁止刷新后悄悄覆盖另一台刚完成的设置。
  const expectedRevision = state.config?.revision
  const device = state.device
  const scope = scopeKey()
  if (!scope || !device || expectedRevision == null || !deviceName.trim()) {
    throw new Error("请先连接打印助手并填写电脑名称")
  }
  try {
    const response = await setLocalPrintPriority({
      deviceId: device.deviceId,
      deviceName: deviceName.trim(),
      preferred,
      expectedRevision,
    })
    if (scope !== scopeKey()) throw new LocalPrintScopeChangedError()
    publish(checkResponse(response))
    window.dispatchEvent(new CustomEvent("local-print-task-created"))
  } catch (error) {
    if (scope === scopeKey()) await refreshLocalPrintPriority().catch(() => {})
    throw error
  }
}

export function startLocalPrintPrioritySync(onReady: () => void) {
  let stopped = false
  let previousReadiness = ""
  const tick = () => refreshLocalPrintPriority(false).then((snapshot) => {
    const readiness = JSON.stringify([
      activeScope,
      snapshot.helperAvailable,
      snapshot.noLocalPrinters,
      snapshot.configured,
      snapshot.device,
      state.config?.revision,
      state.config?.preferredOnline,
    ])
    const changed = readiness !== previousReadiness
    previousReadiness = readiness
    if (!stopped && changed && snapshot.helperAvailable && !snapshot.noLocalPrinters) onReady()
  }).catch(() => {})
  const stopWatch = watch(scopeKey, tick, { immediate: true, flush: "sync" })
  const timer = window.setInterval(tick, DEVICE_SYNC_INTERVAL_MS)
  return () => {
    stopped = true
    stopWatch()
    window.clearInterval(timer)
    generation++
    pending = null
    state.device = null
    invalidateLocalPrintPriority()
  }
}
